import { cn } from '@/lib/utils';
import { formatCount } from '../lib/format';
import type { Tone } from './Badge';

const TONE_TEXT: Partial<Record<Tone, string>> = { warn: 'text-warning', serious: 'text-serious', crit: 'text-critical' };

/**
 * A row of filter tiles, each with its exact count (reference screens 13–14): one click filters
 * the list to that value, the first tile ("Tất cả") clears it. `counts` is null while loading
 * ("…") and undefined when it cannot be read ("—").
 */
export function CountTiles({
  label,
  allLabel,
  options,
  counts,
  current,
  onPick,
}: {
  label: string;
  allLabel: string;
  options: readonly { value: string; label: string; tone?: Tone }[];
  counts: Partial<Record<string, number>> | null | undefined;
  current: string;
  onPick: (value: string) => void;
}) {
  const total = counts ? Object.values(counts).reduce<number>((a, b) => a + (b ?? 0), 0) : 0;
  const tiles = [{ value: '', label: allLabel, tone: undefined as Tone | undefined }, ...options];
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8" role="group" aria-label={label} data-count-tiles>
      {tiles.map((o) => {
        const n = o.value ? (counts?.[o.value] ?? 0) : total;
        const text = counts === null ? '…' : counts === undefined ? '—' : formatCount(n);
        const on = current === o.value;
        return (
          <button
            key={o.value || 'all'}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(o.value)}
            className={cn(
              'flex min-w-0 flex-col items-start gap-1 rounded-xl border bg-card px-3.5 py-2.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
              on && 'border-primary bg-accent',
            )}
          >
            <span className={cn('max-w-full truncate text-xs text-muted-foreground', on && 'text-accent-foreground')}>{o.label}</span>
            <span className={cn('tabular text-lg leading-none font-semibold', counts && n > 0 && o.tone ? TONE_TEXT[o.tone] : '')} data-tile-count={o.value || 'all'}>
              {text}
            </span>
          </button>
        );
      })}
    </div>
  );
}
